import TradingViewChart from "common/Charts/TradingViewChart";
import {
  TIME_RANGE_START,
  TRADE_DRAWER_TIME_RANGES,
} from "@shared/common/constants";
import Flex from "common/Boxes/Flex";
import React, { useContext, useEffect, useMemo, useState } from "react";
import ExchangeSelectionDropdown from "features/portfolio/Assets/ExchangeSelectionDropdown";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import PriceStatistic from "common/Statistic/PriceStatistic";
import styled, { useTheme } from "styled-components";
import StatStrip from "common/NewLook/StatStrip.tsx";
import StatAmount from "common/NewLook/StatAmount.tsx";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";

interface SignalChartProps {
  exchanges: string[];
  symbol?: string;
  isExtension?: boolean;
}

const SignalChart = ({
  exchanges,
  symbol: propsSymbol,
  isExtension,
}: SignalChartProps) => {
  const {
    generalDataQuery,
    setSelectedExchange,
    selectedExchange,
    isEdit,
    pricePrecision,
    currentPrice,
    priceCoin,
    activitiesAnnotations,
  } = useContext(SignalDetailsContext)!;
  const { targetsAnnotations, editSignalLoading } =
    useContext(EditSignalContext)!;
  const { isNewLook } = useTheme();
  const { t: tTrades } = useTypedTranslation(LanguageNameSpaces.TRADES);

  const [symbol, setSymbol] = useState<string>();
  const { socketPrice } = usePricesSocket(symbol, selectedExchange);

  useEffect(() => {
    if (!!propsSymbol && propsSymbol !== symbol) setSymbol(propsSymbol);
  }, [propsSymbol]);

  const allAnnotations = useMemo(() => {
    return [...activitiesAnnotations, ...targetsAnnotations];
  }, [activitiesAnnotations, targetsAnnotations]);

  const exchangeSelector = (
    <ExchangeSelectionDropdown
      setExchange={setSelectedExchange}
      exchange={selectedExchange}
      exchanges={exchanges}
      loading={false}
      disabled={isEdit}
      style={{ marginBottom: 12 }}
    />
  );

  // New look: the current price as a stat strip (as in the trade drawer),
  // the exchange switch at its right, the chart under it.
  if (isNewLook) {
    return (
      <Wrapper
        $justifyContent="flex-start"
        style={{
          padding: isExtension ? "40px 24px" : 0,
          marginBottom: isExtension ? 0 : 24,
          gap: 16,
        }}
      >
        <NewLookStrip>
          <StatStrip
            variant="flat"
            items={[
              {
                key: "price",
                label: tTrades("statistics.current_price"),
                value: (
                  <StatAmount
                    text={socketPrice.value || currentPrice}
                    coin={priceCoin}
                    precision={pricePrecision}
                  />
                ),
              },
            ]}
          />
          <ExchangeSelectionDropdown
            setExchange={setSelectedExchange}
            exchange={selectedExchange}
            exchanges={exchanges}
            loading={false}
            disabled={isEdit}
          />
        </NewLookStrip>
        <ChartWrapper $isExtension={isExtension}>
          <LoadingSpinnerOverlay
            isLoading={!generalDataQuery.currentData || editSignalLoading}
            hideContentWhileLoading={true}
            wrapStyle={{ height: "100%" }}
            wrap={true}
          >
            <TradingViewChart
              symbol={symbol}
              exchange={selectedExchange}
              annotations={allAnnotations}
              timeRanges={TRADE_DRAWER_TIME_RANGES}
              minimize={true}
              showExtendButton={true}
              defaultTimeRange={TIME_RANGE_START}
              startTimestamp={
                generalDataQuery.currentData?.general["Opened at"]
              }
              endTimestamp={generalDataQuery.currentData?.general["Closed at"]}
              object="Signal"
            />
          </LoadingSpinnerOverlay>
        </ChartWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      $justifyContent="flex-start"
      style={{
        padding: isExtension ? "40px 24px" : 0,
        marginBottom: isExtension ? 0 : 24,
      }}
    >
      <PriceStatistic
        precision={pricePrecision}
        currentPrice={currentPrice}
        coin={priceCoin}
        symbol={symbol}
        exchange={selectedExchange}
      />
      <Wrapper $alignItems="flex-end">
        {exchangeSelector}
        <ChartWrapper $isExtension={isExtension}>
          <LoadingSpinnerOverlay
            isLoading={!generalDataQuery.currentData || editSignalLoading}
            hideContentWhileLoading={true}
            wrapStyle={{ height: "100%" }}
            wrap={true}
          >
            <TradingViewChart
              symbol={symbol}
              exchange={selectedExchange}
              annotations={allAnnotations}
              timeRanges={TRADE_DRAWER_TIME_RANGES}
              minimize={true}
              showExtendButton={true}
              defaultTimeRange={TIME_RANGE_START}
              startTimestamp={
                generalDataQuery.currentData?.general["Opened at"]
              }
              endTimestamp={generalDataQuery.currentData?.general["Closed at"]}
              object="Signal"
            />
          </LoadingSpinnerOverlay>
        </ChartWrapper>
      </Wrapper>
    </Wrapper>
  );
};

export default SignalChart;

const Wrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;
  height: 100%;
`;
const ChartWrapper = styled.div<{ $isExtension?: boolean }>`
  width: 100%;
  height: ${(p) => (p.$isExtension ? "100%" : "400px")};
`;
const NewLookStrip = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  > :first-child {
    width: auto;
    flex: 1;
  }
`;
